import {
    getDate,
    getDay,
    getHours,
    getMilliseconds,
    getMinutes,
    getMonth,
    getSeconds,
    getYear,
    getTimestamp,
} from 'owa-datetime';
import type { OwaDate } from 'owa-datetime';
import { type DateStrings, formatDateStrings } from 'owa-datetime-formatters-core';

/** Formats a date according to an OWA-style format string. */
export default function formatDate(
    date: OwaDate | null | undefined,
    format: string | null | undefined,
    strings: DateStrings = formatDateStrings()
): string {
    if (date == void 0) {
        throw new TypeError(`Invalid Date (${date})`);
    }

    if (isNaN(getTimestamp(date))) {
        throw new TypeError('Invalid Date (NaN)');
    }

    if (format == void 0) {
        throw new TypeError(`Invalid Format (${format})`);
    }

    return format.replace(tokens, token => {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS7053 (33,20): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ d: (date: OwaDate) => number; dd: (date: OwaDate) => string; ddd: (date: OwaDate, s: { ddd: string[]; dddd: string[]; MMM: string[]; MMMM: string[]; standaloneMMMM: string[]; ap: string[]; ampm: string[]; AP: string[]; AMPM: string[]; }) => string; ... 19 more ...; TT: (date: OwaDate, s: { ...; }) => string; }'.
        // @ts-expect-error
        const fn = tokenFns[token];
        return fn ? fn(date, strings, format) : token.slice(1, token.length - 1);
    });
}

type Strings = ReturnType<typeof formatDateStrings>;

const d = getDate;
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function pad called at module scope is not allowed. This may cause unwanted side effects. */
const dd = pad(d);
const ddd = (date: OwaDate, s: Strings) => s.ddd[getDay(date)];
const dddd = (date: OwaDate, s: Strings) => s.dddd[getDay(date)];
const M = (date: OwaDate) => getMonth(date) + 1;
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function pad called at module scope is not allowed. This may cause unwanted side effects. */
const MM = pad(M);
const MMM = (date: OwaDate, s: Strings) => s.MMM[getMonth(date)];
const yy = (date: OwaDate) => getYear(date) % 100;
const yyyy = getYear;
const h = (date: OwaDate) => getHours(date) % 12 || 12;
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function pad called at module scope is not allowed. This may cause unwanted side effects. */
const hh = pad(h);
const H = getHours;
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function pad called at module scope is not allowed. This may cause unwanted side effects. */
const HH = pad(H);
const m = getMinutes;
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function pad called at module scope is not allowed. This may cause unwanted side effects. */
const mm = pad(m);
const s = getSeconds;
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function pad called at module scope is not allowed. This may cause unwanted side effects. */
const ss = pad(s);
/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function pad called at module scope is not allowed. This may cause unwanted side effects. */
const l = pad(getMilliseconds, 3);
const amPm = (date: OwaDate, strArray: string[]) => strArray[getHours(date) < 12 ? 0 : 1];
const t = (date: OwaDate, strings: Strings) => amPm(date, strings.AP);
const tt = (date: OwaDate, strings: Strings) => amPm(date, strings.AMPM);
const T = (date: OwaDate, strings: Strings) => amPm(date, strings.ap);
const TT = (date: OwaDate, strings: Strings) => amPm(date, strings.ampm);

function pad(fn: typeof getDate, len: number = 2) {
    return (date: OwaDate) => {
        let str = String(fn(date));
        while (str.length < len) {
            str = '0' + str;
        }
        return str;
    };
}

function MMMM(date: OwaDate, strings: Strings, format: string) {
    let monthNames = strings.MMMM;

    // If the standalone and usual month names are different (more than just case) then we need
    // to decide which one we want to use; this depends on which fields are asked for in the format string.
    if (strings.MMMM[0].toLowerCase() != strings.standaloneMMMM[0].toLowerCase()) {
        let day = false;
        let year = false;

        // Detect which tokens are present in the given format string.
        format.replace(tokens, token => {
            day = day || token == 'd' || token == 'dd';
            year = year || token == 'yy' || token == 'yyyy';
            return '';
        });

        // This feels a little bit hacky, but it is small and self-contained enough to get us by
        // without adding extra dependencies (like having to ask for the user's culture or adding extra strings).
        // If the number of rules grows (which should not, as far as I can tell) then we can revisit this.
        // See this GIST https://gist.github.com/fpintos/827b369da5bd7f268939c54cf259cb87/ for more info
        // on how I came up with these rules.
        const isSimplifiedChinese =
            strings.MMMM[0] == '1 \u6708' && strings.standaloneMMMM[0] == '\u4E00\u6708';

        if (isSimplifiedChinese) {
            // When Simplified Chinese (zh-Hans) has the strings corrected, use standalone name ONLY when just month is requested. Ex:
            // | locale  | MMMM[0] | standaloneMMMM[0] | ex: month only | ex: month+year  | ex: day+month | ex: day+month+year  |
            // | zh-Hans | 1 月    | 一月               | 一月           | 2018 年 1 月    | 1 月 1 日     | 2018 年 1 月 1 日   |
            monthNames = day || year ? strings.MMMM : strings.standaloneMMMM;
        } else {
            // General Rule, use standalone name if only-Month or Month+Year are specified. If day is requested, use MMMM. Ex:
            // | locale | MMMM[0]    | standaloneMMMM[0] | ex: month only | ex: month+year    | ex: day+month  | ex: day+month+year     |
            // | cs     | ledna      | leden             | leden          | leden 2018        | 1. ledna       | 1. ledna 2018          |
            // | el     | Ιανουαρίου | Ιανουάριος        | Ιανουάριος     | Ιανουάριος 2018   | 1 Ιανουαρίου   | 1 Ιανουαρίου 2018      |
            // Note that Intl API seems to format the month-only version in Greek incorrectly, returning Ιανουαρίου (as of 2019-03).
            monthNames = day ? strings.MMMM : strings.standaloneMMMM;
        }
    }

    return monthNames[getMonth(date)];
}

const tokens = /d{1,4}|M{1,4}|yy(?:yy)?|([hHmstT])\1?|l|"[^"]*"|'[^']*'/g;
const tokenFns = {
    d,
    dd,
    ddd,
    dddd,
    M,
    MM,
    MMM,
    MMMM,
    yy,
    yyyy,
    h,
    hh,
    H,
    HH,
    m,
    mm,
    s,
    ss,
    l,
    t,
    tt,
    T,
    TT,
};
